import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { Icon, Input } from '@column/column-ui-kit';

interface SectionResetPasswordProps {
  newPassword: string;
  confirmPassword: string;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
}

// A single bordered box that holds both password inputs stacked and divided by a hairline, like the
// grouped 2FA code inputs. The confirm half animates in (grid-template-rows 0fr -> 1fr) once the user
// starts typing, so the one box appears to "grow" a second field.
const ComboBox = styled.div<{ $hasError: boolean }>`
  position: relative;
  width: 100%;
  border-radius: ${({ theme }) => theme.style.inputBorderRadius};
  background-color: ${({ theme }) => theme.style.inputDefaultBackgroundColor};
  overflow: clip;
  transition: box-shadow 0.2s;
  box-shadow:
    inset 0 0 0 1px ${({ theme }) => theme.style.inputDefaultBorderColor},
    ${({ theme }) => theme.style.depressedShadow};

  &:focus-within {
    box-shadow:
      inset 0 0 0 1px ${({ theme }) => theme.style.inputFocussedBorderColor},
      0 0 0 ${({ theme }) => theme.style.inputFocussedOutline} ${({ theme }) => theme.style.inputFocussedOutlineColor},
      ${({ theme }) => theme.style.depressedShadow};
  }

  ${({ $hasError, theme }) =>
    $hasError &&
    css`
      box-shadow:
        inset 0 0 0 1px ${theme.style.inputErrorBorderColor},
        0 0 0 ${theme.style.inputFocussedOutline} ${theme.style.inputErrorOutlineColor},
        ${theme.style.depressedShadow};
    `}
`;

// Strip each input's own border/background/radius so the fields read as one seamless box. The
// :-webkit-autofill override paints over the blue autofill highlight 1Password/Chrome apply when a
// value is filled, matching the box background so the field stays visually clean.
const FieldInput = styled(Input)`
  && {
    border-radius: 0;
    box-shadow: none;
    background: transparent;
    --input-background: transparent;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active,
  input:autofill {
    -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.style.inputDefaultBackgroundColor} inset !important;
    box-shadow: 0 0 0 1000px ${({ theme }) => theme.style.inputDefaultBackgroundColor} inset !important;
    -webkit-text-fill-color: ${({ theme }) => theme.style.inputDefaultTextColor} !important;
    caret-color: ${({ theme }) => theme.primary.background};
    transition: background-color 9999s ease-in-out 0s;
  }
`;

// The divider between the two fields.
const ConfirmField = styled(FieldInput)`
  && {
    border-top: 1px solid ${({ theme }) => theme.style.inputDefaultBorderColor};
  }
`;

// visibility:hidden (not just the 0fr clip) takes the collapsed field out of the tab order and the
// accessibility tree so keyboard/screen-reader users can't land in the hidden inputs or eye toggle.
// Transitioning visibility keeps it shown through the collapse animation, then hides it at the end.
const Reveal = styled.div<{ show: boolean }>`
  display: grid;
  grid-template-rows: ${({ show }) => (show ? '1fr' : '0fr')};
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  transition:
    grid-template-rows 0.25s ease,
    visibility 0.25s ease;
`;

const RevealInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

// Visibility toggle shown on both fields; either one reveals both at once.
const EyeToggle = styled.button`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 6px;
  margin: -6px -6px -6px 0;
`;

const Eye = styled(Icon.AnimationEye)`
  --eye-line-background-color: ${({ theme }) => theme.style.inputDefaultBackgroundColor};

  svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
    --icon-size: 16px;
  }

  &:hover svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  }
`;

export const SectionResetPassword = ({
  newPassword,
  confirmPassword,
  onNewPasswordChange,
  onConfirmPasswordChange,
}: SectionResetPasswordProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const showConfirm = newPassword.length > 0;
  const hasConfirmError = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const inputType = isVisible ? 'text' : 'password';

  const visibilityToggle = (
    <EyeToggle
      type="button"
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsVisible((v) => !v);
      }}
    >
      <Eye line={!isVisible} />
    </EyeToggle>
  );

  return (
    <ComboBox $hasError={hasConfirmError}>
      <FieldInput
        type={inputType}
        autoComplete="new-password"
        placeholder="New Password"
        value={newPassword}
        onChange={onNewPasswordChange}
      >
        {visibilityToggle}
      </FieldInput>

      <Reveal show={showConfirm}>
        <RevealInner>
          <ConfirmField
            type={inputType}
            autoComplete="new-password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={onConfirmPasswordChange}
            hasError={hasConfirmError}
          >
            {visibilityToggle}
          </ConfirmField>
        </RevealInner>
      </Reveal>
    </ComboBox>
  );
};
